/**
 * Impostazioni dell'applicazione: quello che l'amministratore puo' cambiare
 * dalla pagina Impostazioni senza che nessuno tocchi il codice.
 *
 * Vivono nella tabella `app_settings`, una riga per chiave. Nel database le
 * chiavi sono in italiano con l'underscore (`giorni_ricontatto`), qui dentro
 * in camelCase come tutto il resto del progetto: le due funzioni di
 * conversione in fondo sono l'unico punto in cui i due mondi si toccano.
 */

/** Chiavi delle righe di `app_settings` usate dall'app. */
export const SETTINGS_KEYS = {
    azienda: 'company_profile',
    lead: 'leads_settings',
    fatture: 'invoices_enabled'
} as const;

export interface LeadsSettings {
    /**
     * Dopo quanti giorni senza contatto un lead attivo diventa "da
     * ricontattare". Al doppio dei giorni passa da giallo a rosso.
     */
    giorniRicontatto: number;
    /**
     * Testo gia' scritto nella chat quando si apre WhatsApp da un lead.
     * Vuoto = WhatsApp si apre senza messaggio precompilato.
     */
    messaggioWhatsapp: string;
}

export const LEADS_SETTINGS_DEFAULT: LeadsSettings = {
    giorniRicontatto: 3,
    messaggioWhatsapp: 'Ciao {nome}, ti contatto per la richiesta che ci hai inviato. Posso darti qualche informazione?'
};

/** Limiti di buon senso: un lead "fermo da 0 giorni" o "da 4 anni" non aiuta nessuno. */
export const GIORNI_RICONTATTO_MIN = 1;
export const GIORNI_RICONTATTO_MAX = 90;

/** Segnaposto accettati nel messaggio WhatsApp, con la spiegazione mostrata a schermo. */
export const SEGNAPOSTI_WHATSAPP = [
    { chiave: '{nome}', descrizione: 'nome della persona del lead' },
    { chiave: '{azienda}', descrizione: 'azienda del lead' }
] as const;

/** Dal jsonb del database al tipo usato dall'app, scartando valori non validi. */
export function leggiLeadsSettings(grezzo: unknown): LeadsSettings {
    if (!grezzo || typeof grezzo !== 'object') return LEADS_SETTINGS_DEFAULT;
    const dati = grezzo as Record<string, unknown>;

    const giorni = Number(dati.giorni_ricontatto);
    const messaggio = dati.messaggio_whatsapp;

    return {
        giorniRicontatto:
            Number.isFinite(giorni) && giorni >= GIORNI_RICONTATTO_MIN && giorni <= GIORNI_RICONTATTO_MAX
                ? Math.round(giorni)
                : LEADS_SETTINGS_DEFAULT.giorniRicontatto,
        messaggioWhatsapp:
            typeof messaggio === 'string' ? messaggio : LEADS_SETTINGS_DEFAULT.messaggioWhatsapp
    };
}

/** Dal tipo dell'app al jsonb da salvare. */
export function scriviLeadsSettings(impostazioni: LeadsSettings): Record<string, unknown> {
    return {
        giorni_ricontatto: impostazioni.giorniRicontatto,
        messaggio_whatsapp: impostazioni.messaggioWhatsapp
    };
}

/**
 * Sostituisce i segnaposto col nome e l'azienda del lead.
 * Un segnaposto senza dato sparisce insieme allo spazio che lo precede, cosi'
 * "Ciao {nome}," su un lead senza nome non diventa "Ciao ,".
 */
export function componiMessaggioWhatsapp(
    modello: string,
    lead: { nome?: string | null; azienda?: string | null }
): string {
    const sostituisci = (testo: string, segnaposto: string, valore: string | null | undefined): string =>
        valore?.trim()
            ? testo.split(segnaposto).join(valore.trim())
            : testo.replace(new RegExp(`\\s*${segnaposto.replace(/[{}]/g, '\\$&')}`, 'g'), '');

    const conNome = sostituisci(modello, '{nome}', lead.nome);
    return sostituisci(conNome, '{azienda}', lead.azienda)
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+([,.!?])/g, '$1')
        .trim();
}
